var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageContainer, PageContent } from "@ui/components/wraper";
import PageHeader from "@ui/components/header/PageHeader";
import { SecondaryGrid } from "@ui/components/grid";
import { GridFilters } from "@ui/components/grid/GridFilters";
import { SButton } from "@ui/components/buttons/SButton";
import { usePermissions } from "@ui/hooks/usePermissions";
import { Permission } from "@utility/permission-checker";
import { toast } from "@ui/utils/toaster";
import { categoriesApi } from "@api/enpoints/categories-api";
import { apiErrorMessage } from "@api/enpoints/clients-api";
/** AP and node categories: two tabs over the same server-side grid (search, filters, sort and paging in the API). */
const CategoriesPage = () => {
    const { t } = useTranslation(["common"]);
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const [searchParams, setSearchParams] = useSearchParams();
    const kind = searchParams.get("tab") === "node" ? "node" : "ap";
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState({ pageNumber: 1, pageSize: 25 });
    const [sortState, setSortState] = useState({ field: "name", order: "asc" });
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [filters, setFilters] = useState({});
    const [appliedFilters, setAppliedFilters] = useState({});
    const requestId = useRef(0);
    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(search.trim()), 500);
        return () => clearTimeout(handle);
    }, [search]);
    // Any change of tab, search, filters or sort goes back to the first page.
    useEffect(() => {
        setPage((p) => (Object.assign(Object.assign({}, p), { pageNumber: 1 })));
    }, [kind, debouncedSearch, appliedFilters, sortState]);
    const load = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        const id = ++requestId.current;
        setLoading(true);
        try {
            const response = yield categoriesApi.getAll(kind, {
                filters: {
                    name: appliedFilters.name || undefined,
                    description: appliedFilters.description || undefined,
                    globalSearch: debouncedSearch || undefined,
                },
                pageNumber: page.pageNumber,
                pageSize: page.pageSize,
                sortColumn: sortState === null || sortState === void 0 ? void 0 : sortState.field,
                sortDirection: sortState === null || sortState === void 0 ? void 0 : sortState.order,
            });
            if (id !== requestId.current)
                return; // a newer request is on its way
            setRows((_b = (_a = response.data.data) === null || _a === void 0 ? void 0 : _a.items) !== null && _b !== void 0 ? _b : []);
            setTotal((_d = (_c = response.data.data) === null || _c === void 0 ? void 0 : _c.totalCount) !== null && _d !== void 0 ? _d : 0);
        }
        catch (error) {
            if (id === requestId.current)
                toast.error(apiErrorMessage(error, t("common:catalog.categories.loadFailed", "Could not load the categories.")));
        }
        finally {
            if (id === requestId.current)
                setLoading(false);
        }
    }), [kind, appliedFilters, debouncedSearch, page, sortState, t]);
    useEffect(() => {
        load();
    }, [load]);
    const tabs = useMemo(() => [
        { label: t("common:catalog.categories.apsTab", "APs"), value: "ap" },
        { label: t("common:catalog.categories.nodesTab", "Nodes"), value: "node" },
    ], [t]);
    const handleTabChange = (value) => {
        setSearchParams(value === "node" ? { tab: "node" } : {}, { replace: true });
        setFilters({});
        setAppliedFilters({});
        setSearch("");
    };
    const canCreate = hasPermission(Permission.CATEGORIES_CREATE);
    const canEdit = hasPermission(Permission.CATEGORIES_EDIT);
    const columns = [
        {
            field: "name",
            headerName: kind === "ap"
                ? t("common:catalog.categories.apCategory", "AP Category")
                : t("common:catalog.categories.nodeCategory", "Node Category"),
            flex: 1,
            minWidth: 180,
            filterable: true,
            filterType: "text",
            sortable: true,
        },
        {
            field: "description",
            headerName: t("common:catalog.categories.description", "Description"),
            flex: 2,
            minWidth: 240,
            filterable: true,
            filterType: "text",
            sortable: true,
            renderCell: (params) => params.row.description || "—",
        },
        {
            field: "assignedCount",
            headerName: kind === "ap"
                ? t("common:catalog.categories.assignedAps", "Access Points")
                : t("common:catalog.categories.assignedNodes", "Nodes"),
            flex: 0.6,
            minWidth: 120,
            sortable: true,
        },
        ...(canEdit
            ? [
                {
                    field: "actions",
                    headerName: t("common:grid.actions", "Actions"),
                    minWidth: 120,
                    sortable: false,
                    filterable: false,
                    align: "center",
                    headerAlign: "center",
                    renderCell: (params) => (React.createElement(SButton, { variant: "outlined", size: "small", onClick: (e) => {
                            e.stopPropagation();
                            navigate(`/categories/${kind}/${params.row.id}`);
                        } }, t("common:actions.edit", "Edit"))),
                },
            ]
            : []),
    ];
    const handleSortModelChange = (model) => {
        var _a;
        const first = model[0];
        setSortState(first ? { field: first.field, order: ((_a = first.sort) !== null && _a !== void 0 ? _a : "asc") } : { field: "name", order: "asc" });
    };
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: t("common:catalog.categories.title", "Categories"), topActions: canCreate ? (React.createElement(SButton, { variant: "contained", size: "large", onClick: () => navigate(`/categories/${kind}/new`) }, kind === "ap"
                ? t("common:catalog.categories.addAp", "Add AP Category")
                : t("common:catalog.categories.addNode", "Add Node Category"))) : undefined }),
        React.createElement(PageContent, { sx: { pb: 0 } },
            React.createElement(GridFilters, { loading: loading, columns: columns.filter((c) => c.filterable), filters: filters, onChange: setFilters, searchProp: search, handleSearchChange: (e) => setSearch(e.target.value), tabs: tabs, selectedTab: kind, onTabChange: handleTabChange, sortState: sortState, onSortChange: (field, order) => setSortState({ field, order }), onApplyFilters: (applied) => setAppliedFilters(Object.assign({}, (applied !== null && applied !== void 0 ? applied : filters))), onClearFilters: () => {
                    setFilters({});
                    setAppliedFilters({});
                } }),
            React.createElement(SecondaryGrid, { rows: rows, columns: columns, loading: loading, getRowId: (row) => row.id, paginationMode: "server", sortingMode: "server", filterMode: "server", disableColumnFilter: true, sortModel: sortState ? [{ field: sortState.field, sort: sortState.order }] : [], onSortModelChange: handleSortModelChange, rowCount: total, paginationModel: { page: page.pageNumber - 1, pageSize: page.pageSize }, onPaginationModelChange: (model) => setPage({ pageNumber: model.page + 1, pageSize: model.pageSize }), pageSizeOptions: [5, 25, 50], renderCard: (row) => (React.createElement(Box, { sx: { p: 2, borderRadius: 3, bgcolor: "background.paper", mb: 1 } },
                    React.createElement(Box, { sx: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 } },
                        React.createElement(Typography, { variant: "subtitle1" }, row.name),
                        canEdit && (React.createElement(SButton, { variant: "outlined", size: "small", onClick: () => navigate(`/categories/${kind}/${row.id}`) }, t("common:actions.edit", "Edit")))),
                    row.description && (React.createElement(Typography, { variant: "body2", color: "text.secondary", sx: { mt: 0.5 } }, row.description)))) }))));
};
export default CategoriesPage;
